import React, { useState } from "react";
import {
  TextField,
  Paper,
  Button,
  IconButton,
  Box,
  Typography,
  Switch,
  Grid,
} from "@mui/material";
import { Delete, Save, Edit } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";
import { v4 as uuidv4 } from "uuid";

const PaymentMethodsSetting = ({
  setting,
  editedSettings,
  handleChange,
  handleRemoveItem,
  handleSave,
}: any) => {
  const { t } = useTranslation();
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const getEditedValue = (index: number, field: string) => {
    return (
      editedSettings[setting.key]?.[index]?.[field] ??
      setting.value[index][field]
    );
  };

  const updateSetting = (index: number, field: string, value: any) => {
    const updatedArray = setting.value.map(
      (method, i) =>
        i === index ? { ...method, [field]: value } : { ...method } // 🔥 Derin kopyalama!
    );

    handleChange(setting.key, updatedArray);
  };

  const updateSettingSwitch = (index: number, field: string, value: any) => {
    let updatedArray = setting.value[index]; // Burada doğrudan nesne referansı alınıyor
    updatedArray[field] = value; // Burada doğrudan değişiklik yapılıyor
    setting.value[index] = updatedArray;
    handleChange(setting.key, setting.value); // handleChange fonksiyonuna sadece güncellenmiş tek bir nesne gönderiliyor
  };

  return (
    <Paper
      elevation={3}
      sx={{ p: 3, borderRadius: 2, boxShadow: 3, bgcolor: "white" }}
    >
      <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
        {t(setting.key)}
      </Typography>

      {setting.value.map((method: any, index: number) => {
        const methodId = method.id || uuidv4(); // UUID kullan

        return editingIndex === index ? (
          <Paper
            key={methodId}
            sx={{ p: 2, mb: 2, borderRadius: 2, boxShadow: 2 }}
          >
            <Typography variant="h6" sx={{ mb: 1 }}>
              {t(method.name)}
            </Typography>

            {Object.keys(method).map(
              (field) =>
                field !== "icon" &&
                field !== "name" && (
                  <Box
                    key={`${methodId}-${field}`}
                    sx={{ display: "flex", alignItems: "center", mt: 2 }}
                  >
                    {typeof method[field] === "boolean" ? (
                      <>
                        <Switch
                          checked={getEditedValue(index, field)}
                          onChange={(e) =>
                            updateSettingSwitch(index, field, e.target.checked)
                          }
                        />
                        <Typography sx={{ ml: 1 }}>{t(field)}</Typography>
                      </>
                    ) : (
                      <TextField
                        fullWidth
                        variant="outlined"
                        label={t(field)}
                        value={getEditedValue(index, field)}
                        onChange={(e) =>
                          updateSetting(index, field, e.target.value)
                        }
                        sx={{ mb: 2 }}
                      />
                    )}
                  </Box>
                )
            )}

            <Box
              sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}
            >
              <Button
                variant="contained"
                color="secondary"
                startIcon={<Edit />}
                onClick={() => setEditingIndex(null)}
              >
                {t("cancel")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Save />}
                onClick={() => {
                  handleSave(setting.key);
                  setEditingIndex(null);
                }}
              >
                {t("save_changes")}
              </Button>
            </Box>
          </Paper>
        ) : (
          <Box
            key={methodId}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              bgcolor: "grey.100",
              p: 2,
              borderRadius: 2,
              boxShadow: 2,
              mb: 2,
            }}
          >
            <Grid container spacing={2} alignItems="center">
              {/* Logo */}
              {method.icon && (
                <Grid item>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "white",
                      borderRadius: "50%",
                      boxShadow: 1,
                    }}
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(method.icon),
                    }}
                  />
                </Grid>
              )}

              {/* Ad & Switch */}
              <Grid item xs>
                <Typography sx={{ fontSize: "1.1rem", fontWeight: "medium" }}>
                  {t(method.name)}
                </Typography>
                <Switch
                  checked={getEditedValue(index, "isActive")}
                  onChange={(e) =>
                    updateSettingSwitch(index, "isActive", e.target.checked)
                  }
                />
              </Grid>

              {/* Düzenleme Butonları */}
              <Grid item>
                <IconButton
                  color="primary"
                  onClick={() => setEditingIndex(index)}
                >
                  <Edit />
                </IconButton>
                <IconButton
                  color="secondary"
                  onClick={() => handleRemoveItem(setting.key, index)}
                >
                  <Delete />
                </IconButton>
              </Grid>
            </Grid>
          </Box>
        );
      })}

      {/* Genel Ayarları Düzenle Butonu */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Edit />}
          onClick={() => setEditingIndex(0)} // İlk ödeme yöntemini düzenleme olarak başlat
        >
          {t("edit_settings")}
        </Button>
      </Box>
    </Paper>
  );
};

export default PaymentMethodsSetting;
